import { type Unit, equals as unitsEqual } from '../../units/unit.ts';
import type { RowIdentityType } from './row-identity.ts';

/**
 * Column types and schemas for `Dataset`.
 *
 * `ColumnType` describes the *contents* of a Dataset column — richer than
 * the runtime `Value` kinds in `value.ts` because it must describe
 * container shapes (`timeseries`, `array`, `struct`) and explicit
 * nullability.
 *
 * `Schema = ReadonlyMap<string, ColumnType>`: ordered name →
 * type. Map insertion order becomes the Dataset's column order. Each
 * pipeline verb declares a `propagateSchema` function that maps
 * an input Schema to its output Schema.
 *
 * Conventions:
 *   - `nullable` is the explicit nullability wrapper. Bare types assert
 *     no nulls. Pipeline verbs widen conservatively when they may
 *     introduce nulls (left join, filter on missing, aggregation over
 *     empty groups).
 *   - `unknown` tags inference failures. Downstream type checks skip
 *     `unknown` columns and emit a warning rather than an error.
 *   - `number` vs `integer` preserves origin typing for display
 *     formatting and lossless join semantics, even though all numeric
 *     operations promote to double internally.
 *
 * The leaf kinds (`number`, `integer`, …) are grouped behind `LeafKind`
 * so that the listing is the single source of truth: the union, the
 * runtime membership check, and the doc renderer all derive from
 * `LEAF_KINDS`. Adding a new leaf kind is a one-line change.
 */

const LEAF_KINDS = ['number', 'integer', 'string', 'boolean', 'timestamp', 'date', 'null', 'unknown'] as const;

export type LeafKind = (typeof LEAF_KINDS)[number];

const LEAF_KIND_SET: ReadonlySet<string> = new Set(LEAF_KINDS);
// Type-narrowing predicate: tells TypeScript that a `ColumnType` whose
// `kind` is in `LEAF_KINDS` is the leaf-shaped variant of the union. The
// narrowing matters in `describeColumnType` so the exhaustiveness check
// in the switch's default branch knows every compound kind has been
// handled.
const isLeafColumnType = (t: ColumnType): t is Extract<ColumnType, { kind: LeafKind }> => LEAF_KIND_SET.has(t.kind);

/** Scalar leaf kinds: `LEAF_KINDS` minus the non-data sentinels. */
const SCALAR_LEAF_KINDS = LEAF_KINDS.filter((k) => k !== 'null' && k !== 'unknown');

/**
 * All scalar (orderable / keyable) column kinds. The scalar leaves plus
 * `enum` — structurally compound (it carries `members`) but semantically a
 * `string` refinement, so it orders and keys exactly like a string. SSOT:
 * sort's accept set, the scalar-subfield enumerator, and the recovery-hint
 * prose all derive from this, so they can never drift from one another.
 */
export const SCALAR_KINDS = [...SCALAR_LEAF_KINDS, 'enum'] as const;

const SCALAR_KIND_SET: ReadonlySet<string> = new Set(SCALAR_KINDS);

export type ColumnType =
  | {
      readonly kind: LeafKind;
      /**
       * Canonical-base dimension vector carried downstream — the `Unit` propagation
       * representation. All values flowing through the engine are in this dimension's
       * canonical base (normalization happens once at the model boundary).
       */
      readonly unit?: Unit;
      /**
       * Slug of the unit the raw data is stored in at the source, at ANY scale or
       * representation (e.g. `mscf_per_d`, `mmscf`). Boundary-only metadata: it drives
       * the one-time `storedUnit → canonical base` conversion at materialization and is
       * not used by downstream transforms. Resolves to a {@link UnitDef} via the registry.
       */
      readonly storedUnit?: string;
      /** Optional display-ladder override slug; defaults to the dimension's standard ladder. */
      readonly targetUnit?: string;
    }
  | { readonly kind: 'timeseries'; readonly value: ColumnType }
  | { readonly kind: 'array'; readonly element: ColumnType }
  | { readonly kind: 'enum'; readonly members: readonly string[] }
  | { readonly kind: 'struct'; readonly fields: Readonly<Record<string, ColumnType>> }
  | { readonly kind: 'nullable'; readonly inner: ColumnType }
  | { readonly kind: 'nested-dataset'; readonly innerSchema: Schema }
  | { readonly kind: 'row-identity'; readonly identity: RowIdentityType; readonly targetView?: string }
  | {
      /**
       * A HYDRATED relation: identity-bearing like `row-identity` (its cells still
       * carry `{space, externalId}`, so CDF pushdown + identity joins keep working)
       * but additionally physically hydrated, so it exposes the target view's scalar
       * `fields` as walkable subfields on top of the implicit identity axis.
       */
      readonly kind: 'hydrated-relation';
      readonly identity: RowIdentityType;
      readonly targetView: string;
      readonly fields: Readonly<Record<string, ColumnType>>;
    };

export type Schema = ReadonlyMap<string, ColumnType>;

// Constructor helpers. Nullary kinds are exposed as shared constants
// (identity-stable, cheap equality). Parameterised kinds are factories.

export const numberCol: ColumnType = { kind: 'number' };
export const integerCol: ColumnType = { kind: 'integer' };
export const stringCol: ColumnType = { kind: 'string' };
export const booleanCol: ColumnType = { kind: 'boolean' };
export const timestampCol: ColumnType = { kind: 'timestamp' };
export const dateCol: ColumnType = { kind: 'date' };
export const nullCol: ColumnType = { kind: 'null' };
export const unknownCol: ColumnType = { kind: 'unknown' };

/**
 * Wrap a type as nullable. Idempotent: `nullableCol(nullableCol(x))`
 * returns `nullableCol(x)`. The wrapper is not applied to itself.
 */
export const nullableCol = (inner: ColumnType): ColumnType =>
  inner.kind === 'nullable' ? inner : { kind: 'nullable', inner };

export const timeseriesCol = (value: ColumnType): ColumnType => ({ kind: 'timeseries', value });

export const arrayCol = (element: ColumnType): ColumnType => ({ kind: 'array', element });
/**
 * A categorical column: a `string` cell constrained to a known, finite set
 * of `members`. Enum-ness lives in the TYPE — the value is a plain string —
 * so an enum column refines `string` anywhere a string is accepted, and the
 * value model needs no new kind. `members` is the declared domain, in display
 * order.
 */
export const enumCol = (members: readonly string[]): ColumnType => ({ kind: 'enum', members });

export const structCol = (fields: Readonly<Record<string, ColumnType>>): ColumnType => ({
  kind: 'struct',
  fields,
});

export const rowIdentityCol = (identity: RowIdentityType, targetView?: string): ColumnType => ({
  kind: 'row-identity',
  identity,
  targetView,
});
/**
 * Construct a HYDRATED relation column — identity-bearing (its cells carry
 * `{space, externalId}`) AND physically hydrated with the target view's scalar
 * `fields`, exposed as walkable subfields on top of the implicit identity axis.
 * Provider-agnostic like {@link rowIdentityCol}: the caller supplies the
 * identity contract (CDF passes `CDF_INSTANCE_IDENTITY`).
 */
export const hydratedRelationCol = (
  targetView: string,
  fields: Readonly<Record<string, ColumnType>>,
  identity: RowIdentityType,
): ColumnType => ({
  kind: 'hydrated-relation',
  identity,
  targetView,
  fields,
});

/**
 * A column whose cells are themselves `Dataset`s. The `innerSchema`
 * describes the schema each cell-`Dataset`
 * carries; per-cell verb dispatch in the evaluator rewrites this when a
 * verb is lifted onto the column.
 *
 * Distinct from `timeseries` (which carries a single value `ColumnType` and
 * implies a `{timestamp, value}` shape) — `nested-dataset` is the substrate
 * shape that holds an arbitrary inner schema. A future change may unify
 * `timeseries<T>` as syntactic sugar for `nested-dataset<{timestamp, value: T}>`,
 * but the two coexist for now so domain code can keep referring to
 * `timeseries` without losing the leaf-value annotation.
 */
export const nestedDatasetCol = (innerSchema: Schema): ColumnType => ({ kind: 'nested-dataset', innerSchema });

// Schema builders.

/**
 * Build a Schema from an entries iterable. Map preserves insertion order;
 * that order becomes the column order of the resulting Dataset.
 */
export const schemaFromEntries = (entries: Iterable<readonly [string, ColumnType]>): Schema => new Map(entries);
/**
 * The inner schema of a timeseries-reference / datapoints cell: `{ timestamp,
 * value, isString }`. Per-row unit truth rides on the `value` cells themselves
 * (unit-bearing boxed cells) — there is deliberately NO series-unit string
 * column to drift against them. SSOT for the per-row timeseries cell shape —
 * `viewToSchema`, the timeseries provider, and the engine's per-cell agg-funcs
 * (`value`/`values`/`timestamps`) all build through this so they never drift in
 * column names + types. Parameterised by the `value` cell type (numeric series
 * by default; a string series for `isString` handles).
 */
export const tsRefInnerSchema = (valueType: ColumnType): Schema =>
  schemaFromEntries([
    ['timestamp', timestampCol],
    ['value', valueType],
    ['isString', booleanCol],
  ]);

/** The numeric-`value` datapoints inner schema. SSOT-built via {@link tsRefInnerSchema}. */
export const TS_REF_INNER_SCHEMA: Schema = tsRefInnerSchema(numberCol);

/**
 * Project a Schema down to a subset of column names, preserving the
 * requested order and silently dropping names absent from the source
 * schema. Returns the schema unchanged when `columns` is `undefined`
 * (no projection requested).
 *
 * The instance-identity columns ({@link IDENTITY_COLUMN_NAMES}) are always
 * retained when present in the source schema, even if `columns` omits them:
 * a downstream cross-view set-ref (`(in (:assets %) (col ent externalId))`)
 * binds `externalId`/`space` as ordinary schema columns, so dropping them in
 * a display-oriented projection would silently break the join. Datasets that
 * carry neither (aggregates, transforms) are unaffected — the presence filter
 * elides identity names the source schema lacks.
 *
 * SSOT for every column-list projection in the engine:
 *   - the `(columns ds [:a :b])` verb's data shaping and its pre-eval
 *     `propagateSchema`;
 *   - the CDF view provider, which derives the LazyDataset's *declared*
 *     schema and (at fetch time) the MaterializedDataset's *actual*
 *     schema both via this helper so they cannot diverge (a divergence
 *     would trip the size-strict `schemaRefines` check in `materialize`).
 *
 * Unknown names are silently elided here; callers that need to reject
 * them (the `columns` verb, the `query` op's `referencedColumns` hook)
 * validate up front and raise `UnknownColumnRefError`.
 */
export const projectSchema = (schema: Schema, columns: readonly string[] | undefined): Schema => {
  if (columns === undefined) {
    return schema;
  }
  const retained = [...columns, ...IDENTITY_COLUMN_NAMES.filter((name) => !columns.includes(name))];
  return schemaFromEntries(
    retained
      .map((name) => [name, schema.get(name)] as const)
      .filter((entry): entry is readonly [string, ColumnType] => entry[1] !== undefined),
  );
};

/** The empty Schema — for ops that produce no columns. */
export const emptySchema: Schema = new Map();
/**
 * Build an all-`unknown` schema for the given column names. The fallback
 * shape used at boundaries where the caller doesn't carry an explicit
 * schema — test fixtures, and the arquero boundary's verb output before
 * a verb-specific `propagateSchema` is registered.
 */
export const unknownSchemaFor = (names: Iterable<string>): Schema =>
  schemaFromEntries(Array.from(names, (name) => [name, unknownCol]));

// Predicates.

/**
 * Whether a column type permits null values. `null` itself is nullable;
 * `nullable<...>` is nullable; everything else asserts non-null.
 */
export const isNullable = (t: ColumnType): boolean => t.kind === 'null' || t.kind === 'nullable';

/**
 * Strip the `nullable` wrapper, if present. Returns the inner type
 * (asserted non-null) or the input unchanged. Idempotent.
 */
export const unwrapNullable = (t: ColumnType): ColumnType => (t.kind === 'nullable' ? t.inner : t);

/**
 * Element type of a (possibly nullable) `array` column. SSOT for ops
 * that unwrap one array layer (`first`, `last`, `nth`) — falls back to
 * {@link unknownCol} when the arg isn't a typed array (e.g. `split`'s
 * untyped `unknown` result), so positional access stays well-typed
 * downstream without asserting a bogus element type.
 */
export const arrayElementType = (t: ColumnType): ColumnType => {
  const unwrapped = unwrapNullable(t);
  return unwrapped.kind === 'array' ? unwrapped.element : unknownCol;
};

/**
 * Attach a canonical-base dimension hint to a leaf column type. No-op for non-leaf
 * types or an undefined unit. The unit is metadata only — {@link columnTypesEqual}
 * ignores it.
 */
export const withUnit = (t: ColumnType, unit: Unit | undefined): ColumnType =>
  unit === undefined || !isLeafColumnType(t) ? t : { ...t, unit };

/** The canonical-base dimension of a (possibly nullable) leaf column type, if any. */
export const unitOfType = (t: ColumnType): Unit | undefined => {
  const leaf = unwrapNullable(t);
  return isLeafColumnType(leaf) ? leaf.unit : undefined;
};
/**
 * Replace the unit hint on a (possibly nullable) leaf column type: attach
 * `unit` when defined, strip an existing hint when `undefined`. The
 * nullable wrapper is transparent — the hint lives on the inner leaf; other
 * non-leaf types pass through untouched. SSOT for every site that must NOT
 * thread a declared unit through a computation (unit truth is value-level:
 * boxed cells in, `refineSchema` derivation out).
 */
export const replaceUnit = (t: ColumnType, unit: Unit | undefined): ColumnType => {
  if (t.kind === 'nullable') {
    return nullableCol(replaceUnit(t.inner, unit));
  }
  if (!isLeafColumnType(t)) {
    return t;
  }
  if (unit === undefined && t.unit === undefined) {
    return t;
  }
  const { unit: _stripped, ...rest } = t;
  return unit === undefined ? rest : { ...rest, unit };
};

/**
 * Field-wise unit unification over same-kind column types: the shared unit
 * when EVERY contributor carries an equal one; any mismatch — differing
 * units, or a unitless contributor alongside a unit-bearing one — yields
 * `undefined` (unit dropped, kind kept by the caller). Shared by the two
 * type unifiers (`unifyInferredTypes` in `schema/refine.ts` and
 * `unifyColumnTypes` in `schema/infer.ts`).
 */
export const unifiedUnitOfTypes = (types: readonly ColumnType[]): Unit | undefined => {
  const [first, ...rest] = types.map(unitOfType);
  if (first === undefined) {
    return undefined;
  }
  return rest.every((u) => u !== undefined && unitsEqual(u, first)) ? first : undefined;
};

/**
 * The unit to DISPLAY for a column type: {@link unitOfType} for a leaf, and for a
 * timeseries-reference column the unit of its inner `value` leaf — the datapoint
 * scalar the series carries (stamped on the inner schema, where `tsRefInnerSchema`
 * keys it as `value`). Display-only: deliberately distinct from {@link unitOfType},
 * the unit-propagation / aggregation primitive, which MUST keep treating a
 * dataset-valued (`nested-dataset` / `timeseries`) column as unit-less so arithmetic
 * and `sum` / `mean` never fabricate a unit from the inner leaf. Use only on the
 * schema-doc display path (`displaySymbolForType`).
 */
export const displayUnitOfType = (t: ColumnType): Unit | undefined => {
  const direct = unitOfType(t);
  if (direct !== undefined) {
    return direct;
  }
  const inner = unwrapNullable(t);
  if (inner.kind === 'nested-dataset') {
    const value = inner.innerSchema.get('value');
    return value === undefined ? undefined : unitOfType(value);
  }
  return inner.kind === 'timeseries' ? unitOfType(inner.value) : undefined;
};

/**
 * Attach a stored-unit slug to a leaf column type — boundary metadata recording the
 * unit the raw data arrives in. No-op for non-leaf types or an undefined slug. Like
 * {@link withUnit}, it is non-structural and {@link columnTypesEqual} ignores it.
 */
export const withStoredUnit = (t: ColumnType, storedUnit: string | undefined): ColumnType =>
  storedUnit === undefined || !isLeafColumnType(t) ? t : { ...t, storedUnit };

/** The stored-unit slug of a (possibly nullable) leaf column type, if any. */
export const storedUnitOfType = (t: ColumnType): string | undefined => {
  const leaf = unwrapNullable(t);
  return isLeafColumnType(leaf) ? leaf.storedUnit : undefined;
};

/**
 * The instance-identity column names every CDF node carries — the
 * `{ space, externalId }` axis `viewToSchema` surfaces as top-level scalar
 * columns. The single SSOT for: the columns {@link projectSchema} always
 * retains (so a downstream cross-view set-ref can bind `externalId`/`space`
 * even when a projection omits them), the `row-identity` relation cell's
 * walkable subfields ({@link ROW_IDENTITY_FIELDS}), and the view layer's
 * node-level key handling (`resolveColumnSelector` / `parseView`).
 */
export const IDENTITY_COLUMN_NAMES = ['space', 'externalId'] as const;

/**
 * The implicit walkable subfields of a `row-identity` relation cell — the
 * `{space, externalId}` instance reference the provider materialises for a
 * direct relation. Derived from {@link IDENTITY_COLUMN_NAMES}; SSOT-consumed
 * via {@link walkableFields}.
 */
const ROW_IDENTITY_FIELDS: Readonly<Record<string, ColumnType>> = Object.fromEntries(
  IDENTITY_COLUMN_NAMES.map((name) => [name, stringCol]),
);

/**
 * The walkable subfield map of a (possibly nullable) column type: declared
 * `fields` for a `struct`, the implicit `{space, externalId}` instance-identity
 * fields for a `row-identity` relation reference, those identity fields UNIONED
 * with the hydrated target view's scalar `fields` for a `hydrated-relation`,
 * `undefined` for a type with no walkable subfields. SSOT for every static
 * sub-path walk — `(col …)` refs, join `:on` keys, sort-key hints, and set-ref
 * inlining all resolve subfields through this map, so struct and relation
 * columns walk identically. A `hydrated-relation` exposes its hydrated fields ON
 * TOP of the identity axis while staying identity-bearing (see
 * {@link identityElement}); a bare `row-identity` exposes ONLY the identity
 * axis, so `(col … name)` off a bare relation is rejected loudly.
 */
export const walkableFields = (t: ColumnType): Readonly<Record<string, ColumnType>> | undefined => {
  const u = unwrapNullable(t);
  if (u.kind === 'struct') {
    return u.fields;
  }
  if (u.kind === 'row-identity') {
    return ROW_IDENTITY_FIELDS;
  }
  if (u.kind === 'hydrated-relation') {
    return { ...ROW_IDENTITY_FIELDS, ...u.fields };
  }
  return undefined;
};
/**
 * The SSOT identity accessor: the row-identity descriptor a column carries iff
 * it is identity-bearing, else `undefined`. Both a bare `row-identity` and a
 * `hydrated-relation` answer "yes" — identity-ness lives in the TYPE KIND, never
 * a struct field-name sniff. Every "is this a relation / does this carry a row
 * identity" test (set-ref inlining, pushdown relation classification, join
 * keying) funnels through here so a new identity-bearing kind is recognised in
 * exactly one place. Walks through a `nullable` wrapper.
 */
export const identityElement = (
  t: ColumnType | undefined,
): { readonly identity: RowIdentityType; readonly targetView?: string } | undefined => {
  if (t === undefined) {
    return undefined;
  }
  const u = unwrapNullable(t);
  if (u.kind === 'row-identity' || u.kind === 'hydrated-relation') {
    return { identity: u.identity, targetView: u.targetView };
  }
  return undefined;
};

/** True iff `t` is identity-bearing (a bare or hydrated relation). SSOT via {@link identityElement}. */
export const isIdentityBearing = (t: ColumnType | undefined): boolean => identityElement(t) !== undefined;
/**
 * Walk one segment off a column type: the field's type for a `struct` /
 * relation walkable subfield, or — for an `array<element>` — the field walked
 * off the ELEMENT re-wrapped as `array<fieldType>` (the MAP semantics of
 * `(col % arr field)` projecting a field across every element). `undefined`
 * when the segment leaves walkable territory or names no field. Composes for
 * nested arrays. SSOT for the per-segment walk shared by {@link structPathType}.
 */
export const walkFieldType = (type: ColumnType, seg: string): ColumnType | undefined => {
  const u = unwrapNullable(type);
  if (u.kind === 'array') {
    const inner = walkFieldType(u.element, seg);
    return inner === undefined ? undefined : arrayCol(inner);
  }
  return walkableFields(u)?.[seg];
};

/**
 * Walk a column type down `path` (each segment a walkable subfield — a struct
 * field, a relation's implicit instance-identity field, or a field MAPPED
 * across an `array` element), unwrapping `nullable` wrappers as it descends.
 * Returns the type at the path end, or `undefined` when a segment leaves
 * walkable territory or names no field. The TOLERANT counterpart to the strict
 * path walks in `(col …)` / `join :on` (which throw a typed error on the same
 * shape); used where an unwalkable type simply means "no match" rather than a
 * query error.
 */
export const structPathType = (start: ColumnType | undefined, path: readonly string[]): ColumnType | undefined =>
  path.reduce<ColumnType | undefined>((type, seg) => {
    if (type === undefined) {
      return undefined;
    }
    return walkFieldType(type, seg);
  }, start);

/**
 * True iff `t` is a temporal column (`timestamp` / `date`), looking
 * through a `nullable` wrapper. SSOT for "should date-aware handling
 * kick in for this column" — used by the sort verb (instant ordering)
 * and the CDF view provider's filter pushdown (ISO normalization).
 */
export const isTemporalColumn = (t: ColumnType): boolean => {
  const u = unwrapNullable(t);
  return u.kind === 'timestamp' || u.kind === 'date';
};
/**
 * True iff `t` is a numeric column (`number` / `integer`), looking
 * through a `nullable` wrapper. SSOT for "this column carries scalar
 * numeric data" — used by window-op-on-nested-dataset target-col
 * resolution (pick `:value` for raw TS-ref or the lone numeric inner
 * col for post-bucket cells) and any future numeric-only-op dispatch.
 */
export const isNumericColumn = (t: ColumnType): boolean => {
  const u = unwrapNullable(t);
  return u.kind === 'number' || u.kind === 'integer';
};

/** True iff `t` is a scalar (orderable/keyable) column, through a nullable wrapper. */
export const isScalarColumn = (t: ColumnType): boolean => SCALAR_KIND_SET.has(unwrapNullable(t).kind);

/** A column's directly-scalar walkable subfield names — the legal sort/join/group sub-paths. */
export const scalarSubfields = (t: ColumnType): readonly string[] =>
  Object.entries(walkableFields(t) ?? {})
    .filter(([, ft]) => isScalarColumn(ft))
    .map(([f]) => f);

// Rendering.

/**
 * Single source of truth for walking the `ColumnType` union. Routes a
 * `ColumnType` to exactly one handler keyed by its variant: the leaf
 * shape (any {@link LeafKind}) plus each compound kind. The lone
 * exhaustive switch lives here, so any function that needs to dispatch
 * over the union (rendering, structural equality, …) shares one
 * enumeration of the kinds. Adding a new compound `ColumnType` kind
 * forces a new handler in `ColumnTypeFolder` and a new `case` here —
 * a compile error in every fold-based walker until both are updated.
 */
type ColumnTypeFolder<ResultType> = {
  readonly leaf: (t: Extract<ColumnType, { kind: LeafKind }>) => ResultType;
  readonly nullable: (t: Extract<ColumnType, { kind: 'nullable' }>) => ResultType;
  readonly timeseries: (t: Extract<ColumnType, { kind: 'timeseries' }>) => ResultType;
  readonly array: (t: Extract<ColumnType, { kind: 'array' }>) => ResultType;
  readonly enum: (t: Extract<ColumnType, { kind: 'enum' }>) => ResultType;
  readonly struct: (t: Extract<ColumnType, { kind: 'struct' }>) => ResultType;
  readonly 'nested-dataset': (t: Extract<ColumnType, { kind: 'nested-dataset' }>) => ResultType;
  readonly 'row-identity': (t: Extract<ColumnType, { kind: 'row-identity' }>) => ResultType;
  readonly 'hydrated-relation': (t: Extract<ColumnType, { kind: 'hydrated-relation' }>) => ResultType;
};

const foldColumnType = <ResultType>(t: ColumnType, handlers: ColumnTypeFolder<ResultType>): ResultType => {
  if (isLeafColumnType(t)) {
    return handlers.leaf(t);
  }
  switch (t.kind) {
    case 'nullable':
      return handlers.nullable(t);
    case 'timeseries':
      return handlers.timeseries(t);
    case 'array':
      return handlers.array(t);
    case 'enum':
      return handlers.enum(t);
    case 'struct':
      return handlers.struct(t);
    case 'nested-dataset':
      return handlers['nested-dataset'](t);
    case 'row-identity':
      return handlers['row-identity'](t);
    case 'hydrated-relation':
      return handlers['hydrated-relation'](t);
    default: {
      // Exhaustiveness: leaf kinds are caught by the membership check
      // above; every compound kind has its own case. Adding a new
      // compound kind without a case breaks this assignment.
      const _exhaustive: never = t;
      return _exhaustive;
    }
  }
};

/**
 * Render a timeseries datapoint value type for the agent-facing docs. A
 * datapoint value is always scalar (string or number, never a struct/array);
 * a discriminator-less `TimeSeries` ref types its value as `unknown` only to
 * defer the string-vs-number decision to fetch (resolved there from the
 * authoritative `isString` flag). Describe that as `string | number` rather
 * than the opaque `unknown`, so the agent knows the series is readable.
 */
const describeTimeseriesValue = (value: ColumnType): string =>
  value.kind === 'unknown' ? 'string | number' : describeColumnType(value);
/**
 * Human-readable rendering of a `ColumnType`. Used by diagnostics and
 * the LLM-facing documentation surface.
 *
 *   number                          → "number"
 *   nullable(number)                → "number?"
 *   timeseries(number)              → "timeseries<number>"
 *   array(struct{a: number})        → "array<struct{ a: number }>"
 *   enum(FCT, WC, unknown)          → "enum<FCT | WC | unknown>"
 */
export const describeColumnType = (t: ColumnType): string =>
  foldColumnType(t, {
    leaf: (leaf) => leaf.kind,
    nullable: (n) => `${describeColumnType(n.inner)}?`,
    timeseries: (ts) => `timeseries<${describeTimeseriesValue(ts.value)}>`,
    array: (a) => `array<${describeColumnType(a.element)}>`,
    enum: (e) => `enum<${e.members.join(' | ')}>`,
    struct: (s) => {
      const inner = Object.entries(s.fields)
        .map(([k, v]) => `${k}: ${describeColumnType(v)}`)
        .join(', ');
      return `struct{ ${inner} }`;
    },
    'nested-dataset': (nd) => describeNestedDataset(nd.innerSchema),
    'row-identity': (ri) => describeRowIdentity(ri),
    'hydrated-relation': (hr) => {
      const inner = Object.entries(hr.fields)
        .map(([k, v]) => `${k}: ${describeColumnType(v)}`)
        .join(', ');
      return `reference<${hr.targetView}>{ ${inner} }`;
    },
  });

/**
 * A row-identity rendering: render a `row-identity` column by a named shape,
 * or `undefined` to defer to the next renderer / the bare-identity fallback.
 */
type RowIdentityRenderer = (t: Extract<ColumnType, { kind: 'row-identity' }>) => string | undefined;

/**
 * Composable registry of row-identity renderings, tried in order. The first
 * match wins, else {@link describeRowIdentity} falls back to a bare identity
 * label keyed by the identity contract's id.
 */
const ROW_IDENTITY_RENDERERS: readonly RowIdentityRenderer[] = [
  (t) => (t.targetView === undefined ? undefined : `reference<${t.targetView}>`),
];

const describeRowIdentity = (t: Extract<ColumnType, { kind: 'row-identity' }>): string => {
  const rendered = ROW_IDENTITY_RENDERERS.map((render) => render(t)).find((label) => label !== undefined);
  return rendered ?? `identity<${t.identity.id}>`;
};

/**
 * Human-readable rendering of a whole Schema. `(empty)` for the empty
 * schema; otherwise `name: type, name: type, ...` in insertion order.
 */
export const describeSchema = (schema: Schema): string => {
  if (schema.size === 0) {
    return '(empty)';
  }
  return Array.from(schema.entries())
    .map(([name, type]) => `${name}: ${describeColumnType(type)}`)
    .join(', ');
};
/**
 * A nested-dataset element shorthand: render `inner` by a named element shape,
 * or `undefined` to defer to the next shorthand / the full-object fallback.
 */
type NestedDatasetShorthand = (inner: Schema) => string | undefined;

/**
 * Declare a shorthand for a PARAMETERISED nested-dataset shape: lift the cell
 * type at `valueKey`, rebuild the canonical shape via its SSOT `build`er, and —
 * when `inner` structurally equals that rebuild — `render` it by name. The shape
 * is never re-spelled here; it is taken from `build`, so the two cannot drift.
 */
const parameterizedShape = (
  valueKey: string,
  build: (value: ColumnType) => Schema,
  render: (value: ColumnType) => string,
): NestedDatasetShorthand => {
  return (inner) => {
    const value = inner.get(valueKey);
    return value !== undefined && schemasEqual(inner, build(value)) ? render(value) : undefined;
  };
};

/**
 * Composable registry of nested-dataset element shorthands, tried in order.
 * Add an entry to give another inner-schema shape a named rendering; the first
 * match wins, else {@link describeNestedDataset} falls back to the full
 * `dataset<{…}>` object shape.
 */
const NESTED_DATASET_SHORTHANDS: readonly NestedDatasetShorthand[] = [
  // A datapoints inner schema is a timeseries handle the agent must resolve.
  parameterizedShape('value', tsRefInnerSchema, (value) => `timeseries<${describeTimeseriesValue(value)}>`),
];

/**
 * Render a nested-dataset column by its element shape: the first matching
 * {@link NESTED_DATASET_SHORTHANDS} entry, else the full inner-object shape.
 */
const describeNestedDataset = (inner: Schema): string =>
  NESTED_DATASET_SHORTHANDS.reduce<string | undefined>((acc, match) => acc ?? match(inner), undefined) ??
  `dataset<${describeSchema(inner)}>`;

/**
 * Agent-facing documentation for a single column. The type vocabulary is the
 * canonical engine {@link ColumnType}; `referencedType` names the related view
 * for relation and timeseries-reference columns so the projector can render
 * them by name instead of dumping their structural shape.
 */
export interface ColumnDoc {
  /** Column key as it appears in each row record. */
  readonly name: string;
  /** Value type carried by this column. */
  readonly type: ColumnType;
  /** Human-readable description propagated from upstream schemas. */
  readonly description?: string;
  /** Related view name, present for relation and timeseries-reference columns. */
  readonly referencedType?: string;
}

/**
 * Agent-facing documentation for one (CDF or virtual) view: its description and
 * typed columns. Produced by a provider's `describe` capability and rendered by
 * the DescribeViews tool.
 */
export interface ViewDescription {
  /** View-level description (omitted ⇒ the caller supplies a fallback). */
  readonly description?: string;
  /** The view's columns with type and per-column description. */
  readonly columns: readonly ColumnDoc[];
}

// A relation cell rendered by its referenced view name. Shared by the two
// representations a relation can carry: an opaque `row-identity` (a bare CDF
// relation) and a `struct` (an enriched ancestor, which also exposes its
// projectable subfields in the column description).
const referenceByName = (_type: ColumnType, referencedType: string): string => `reference<${referencedType}>`;

const REFERENCED_TYPE_RENDERERS: Partial<
  Record<ColumnType['kind'], (type: ColumnType, referencedType: string) => string>
> = {
  'nested-dataset': (type, referencedType) => {
    const valueType = type.kind === 'nested-dataset' ? type.innerSchema.get('value') : undefined;
    const valueLabel = valueType === undefined ? 'string | number' : describeTimeseriesValue(valueType);
    return `timeseries<${valueLabel}><${referencedType}>`;
  },
  'row-identity': referenceByName,
  'hydrated-relation': referenceByName,
  struct: referenceByName,
  array: (_type, referencedType) => `array<reference<${referencedType}>>`,
};

/**
 * The single agent-facing type-string SSOT for a {@link ColumnDoc}. Wraps
 * {@link describeColumnType} but, when `referencedType` is set, renders
 * relations and timeseries references by name rather than dumping their
 * structural shape — detection keys on `referencedType` presence plus the
 * structural kind, never on the typename, so TS-ness stays structural:
 *
 *   direct relation (row-identity)                      → "reference<CogniteAsset>"
 *   relation list   (array<row-identity>)               → "array<reference<CogniteAsset>>"
 *   timeseries ref  (nested-dataset)                    → "timeseries<number><CogniteTimeSeries>"
 *
 * Everything else falls through to {@link describeColumnType} unchanged.
 */
export const describeColumnDocType = (doc: ColumnDoc): string => {
  const { referencedType } = doc;
  if (referencedType === undefined) {
    return describeColumnType(doc.type);
  }
  const renderer = REFERENCED_TYPE_RENDERERS[doc.type.kind];
  if (renderer === undefined) {
    return describeColumnType(doc.type);
  }
  return renderer(doc.type, referencedType);
};

/**
 * Allocation-free field-set equality on two `Record<string, ColumnType>`
 * maps: every `af` field must match in `bf`, and `bf` may carry no extra
 * field. Object-key order is irrelevant. The shared SSOT for the `struct`
 * and `hydrated-relation` arms of {@link columnTypesEqual}.
 */
const fieldsEqual = (af: Readonly<Record<string, ColumnType>>, bf: Readonly<Record<string, ColumnType>>): boolean => {
  // Allocation-free: no Object.entries/keys arrays, no closure.
  for (const k in af) {
    const aType = af[k];
    const bType = bf[k];
    if (aType === undefined || bType === undefined || !columnTypesEqual(aType, bType)) {
      return false;
    }
  }
  for (const k in bf) {
    if (!(k in af)) {
      return false;
    }
  }
  return true;
};

/**
 * Structural equality on a single `ColumnType`. Identity-first — all leaf
 * types are shared singletons (`numberCol`, …) and compound subtrees are
 * frequently reference-shared (projectSchema / propagateSchema thread the
 * same `ColumnType` objects through), so a reference compare resolves the
 * hot path in one pointer check. Then discriminator, then recursive on
 * wrapper variants:
 *   - leaves equal iff they share `kind`;
 *   - `nullable` / `timeseries` / `array` recurse on their inner type;
 *   - `enum` requires the same member SET (declared order is a display
 *     concern, not part of type identity);
 *   - `struct` requires identical field-name sets and recursive equality
 *     of each field's type (object-key order irrelevant);
 *   - `nested-dataset` recurses via `schemasEqual` on the inner schema.
 *
 * Used by the materializer to check that the
 * verb-chain output schema matches the `LazyDataset`'s declared schema,
 * catching `propagateSchema` bugs at the materialisation boundary.
 */
export const columnTypesEqual = (a: ColumnType, b: ColumnType): boolean => {
  // Identity fast-path: types are immutable, so a shared reference is
  // conclusively equal. Leaves are all singletons and compound subtrees
  // are frequently reference-shared, so this resolves the dominant case
  // with a single pointer compare — no kind compare, no recursion.
  if (a === b) {
    return true;
  }
  if (a.kind !== b.kind) {
    return false;
  }
  // Non-singleton leaf (e.g. a hand-built literal): equal discriminator
  // ⇒ equal. This guard also narrows `a` to the compound variants, so the
  // switch below stays exhaustive — a new compound kind has no `case`,
  // falls into `default`, and trips the `never` assignment at compile time
  // (the same single-source-of-truth guarantee the fold gave us).
  if (isLeafColumnType(a)) {
    return true;
  }
  switch (a.kind) {
    case 'nullable':
      return columnTypesEqual(a.inner, (b as { inner: ColumnType }).inner);
    case 'timeseries':
      return columnTypesEqual(a.value, (b as { value: ColumnType }).value);
    case 'array':
      return columnTypesEqual(a.element, (b as { element: ColumnType }).element);
    case 'enum': {
      const bm = (b as { members: readonly string[] }).members;
      // Same member set ⇒ equal. Members are unique, so a length check plus
      // subset-containment is full set equality, order-insensitive.
      if (a.members.length !== bm.length) {
        return false;
      }
      const bs = new Set(bm);
      for (const m of a.members) {
        if (!bs.has(m)) {
          return false;
        }
      }
      return true;
    }
    case 'struct':
      return fieldsEqual(a.fields, (b as { fields: Readonly<Record<string, ColumnType>> }).fields);
    case 'nested-dataset':
      return schemasEqual(a.innerSchema, (b as { innerSchema: Schema }).innerSchema);
    case 'row-identity': {
      const bri = b as { identity: RowIdentityType; targetView?: string };
      return a.identity.id === bri.identity.id && a.targetView === bri.targetView;
    }
    case 'hydrated-relation': {
      const bh = b as { identity: RowIdentityType; targetView: string; fields: Readonly<Record<string, ColumnType>> };
      return a.identity.id === bh.identity.id && a.targetView === bh.targetView && fieldsEqual(a.fields, bh.fields);
    }
    default: {
      const _exhaustive: never = a;
      return _exhaustive;
    }
  }
};

/**
 * Structural equality on `Schema` (an ordered map of name → ColumnType).
 * Equal iff the two maps have the same set of keys (order-independent —
 * insertion order is meaningful for rendering but not for equivalence)
 * and `columnTypesEqual` for every key's type.
 *
 * SSOT for "is this schema the same shape as that one" — used by the
 * materializer's post-condition check.
 */
export const schemasEqual = (a: Schema, b: Schema): boolean => {
  // Identity fast-path: a shared Map reference is conclusively equal.
  if (a === b) {
    return true;
  }
  if (a.size !== b.size) {
    return false;
  }
  for (const [name, type] of a) {
    const other = b.get(name);
    if (other === undefined || !columnTypesEqual(type, other)) {
      return false;
    }
  }
  return true;
};
/**
 * Directional compatibility: does `resolved` (the runtime-true schema)
 * *refine* `declared` (the statically-propagated prediction)?
 *
 * `propagateSchema` is a conservative LOWER BOUND — `unknown` literally
 * means "predicted: don't know". Runtime discovering a concrete type
 * for an `unknown`-predicted column *fulfils* the prediction, it does
 * not contradict it. So:
 *
 *   - declared column `unknown` (through any `nullable` wrapper) ⇒ any
 *     resolved type is accepted;
 *   - resolved column `unknown` ⇒ accepted (runtime simply couldn't
 *     refine it — never a contradiction, and leniency here only ever
 *     *reduces* false `MaterializationError`s vs. the old equality);
 *   - a resolved `enum` fulfils a declared `string` — an enum value IS a
 *     string, so a column declared `string` is satisfied by a runtime that
 *     narrowed it to a known member set;
 *   - both concrete ⇒ structural equality, `nullable`-insensitive
 *     (nullability is data-dependent, not a propagator contract) — EXCEPT
 *     a `nested-dataset` on both sides, whose inner schemas are compared
 *     by `schemaRefines` (recursively) rather than strict equality, so the
 *     `unknown → concrete` leniency propagates INTO the inner schema. A
 *     dataset declared with an inner `value:unknown` cell (a top-level
 *     datapoints query, where one selection may mix numeric and string
 *     series) is fulfilled by a fetched inner `value:number` — without the
 *     recursion the strict inner equality would spuriously reject it.
 *
 * Serves as the schema post-condition in `materialize` (its only
 * behavioural caller) so value-based schema refinement can make a
 * materialised schema MORE specific than the plan predicted without
 * tripping a spurious mismatch — while a genuine propagator
 * bug (declared `number`, resolved `string`) is still caught. Equality
 * call-sites that need STRICT inner-schema matching (value-unification,
 * group-key identity) keep using `columnTypesEqual` directly and are
 * unaffected by this refinement-only leniency.
 */
export const columnTypeRefines = (resolved: ColumnType, declared: ColumnType): boolean => {
  if (unwrapNullable(declared).kind === 'unknown' || unwrapNullable(resolved).kind === 'unknown') {
    return true;
  }
  const r = unwrapNullable(resolved);
  const d = unwrapNullable(declared);
  if (r.kind === 'nested-dataset' && d.kind === 'nested-dataset') {
    return schemaRefines(r.innerSchema, d.innerSchema);
  }
  if (r.kind === 'enum' && d.kind === 'string') {
    return true;
  }
  return columnTypesEqual(r, d);
};

/**
 * Per-column {@link columnTypeRefines} over a whole `Schema`. Same
 * key-set requirement as {@link schemasEqual} (a verb chain must not
 * drop or invent columns), but each column is checked directionally.
 */
export const schemaRefines = (resolved: Schema, declared: Schema): boolean => {
  if (resolved.size !== declared.size) {
    return false;
  }
  for (const [name, declaredType] of declared) {
    const resolvedType = resolved.get(name);
    if (resolvedType === undefined || !columnTypeRefines(resolvedType, declaredType)) {
      return false;
    }
  }
  return true;
};
/**
 * The most-concrete unification of two refinement-compatible `ColumnType`s —
 * the dual of {@link columnTypeRefines}. Where one side is `unknown` the other,
 * concrete side wins; two `nested-dataset`s recurse field-wise via
 * {@link schemaJoin} (so a `timeseries<unknown>` joined with a
 * `timeseries<number>` becomes `timeseries<number>`); on a concrete-vs-concrete
 * pair `declared` wins structurally, but units merge EVIDENCE-FIRST: the
 * `resolved` side's unit is cell-derived (`refineSchema` over actual boxed
 * values), so it outranks the declaration; the declared unit fills in only
 * where the resolved side is unit-ignorant. One rule serves both join
 * polarities — the query facade (declared carries the promoted unit, resolved
 * is a raw unit-less expansion) and materialize's replay (resolved carries the
 * refined unit, the static declaration is unit-less by design).
 *
 * PRECONDITION: `columnTypeRefines(resolved, declared)`. Callers assert
 * `schemaRefines` first, so the pair is always compatible.
 */
export const columnTypeJoin = (resolved: ColumnType, declared: ColumnType): ColumnType => {
  if (unwrapNullable(declared).kind === 'unknown') {
    return resolved;
  }
  if (unwrapNullable(resolved).kind === 'unknown') {
    return declared;
  }
  const r = unwrapNullable(resolved);
  const d = unwrapNullable(declared);
  if (r.kind === 'nested-dataset' && d.kind === 'nested-dataset') {
    const joined = nestedDatasetCol(schemaJoin(r.innerSchema, d.innerSchema));
    return resolved.kind === 'nullable' || declared.kind === 'nullable' ? nullableCol(joined) : joined;
  }
  return replaceUnit(declared, unitOfType(resolved) ?? unitOfType(declared));
};

/**
 * Per-column {@link columnTypeJoin} over a whole `Schema`: the most-concrete
 * unification of two refinement-compatible schemas, the dual of
 * {@link schemaRefines} (its asserted precondition). Iterates `resolved`'s key
 * order — the live column order of the dataset being re-stamped — joining each
 * column against `declared`. Used by the `query` facade to re-stamp its
 * authoritative declared (unit-bearing) types onto an expansion whose static
 * propagation left them `unknown`.
 */
export const schemaJoin = (resolved: Schema, declared: Schema): Schema =>
  schemaFromEntries(
    Array.from(resolved, ([name, resolvedType]: readonly [string, ColumnType]): readonly [string, ColumnType] => {
      const declaredType = declared.get(name);
      return [name, declaredType === undefined ? resolvedType : columnTypeJoin(resolvedType, declaredType)];
    }),
  );
